import { z } from 'zod';

/**
 * What an appointment costs up front, and what happens to it if the customer
 * doesn't turn up.
 *
 * ## Why this exists
 *
 * Appointments booked for free (SPEC D10). A customer could hold a bay slot and
 * simply not arrive, at zero cost to them and a real cost to the operator — the
 * slot is gone, and nobody else could book it. The owner decided on 2026-07-22
 * that booking payment should be operator-configurable: collect the FULL price
 * or a configured PERCENTAGE at book time, on the existing toyyibPay rail.
 *
 * ## The rules this file encodes
 *
 * 1. **Off by default.** `mode: 'none'` charges nothing and behaves exactly as
 *    appointments did before. Every tunable is an operator setting with a safe
 *    default, never a hardcoded number (feedback: settings live in the admin
 *    dashboard). Turning it on is a deliberate act by an operator who has
 *    decided their no-show rate justifies asking customers for money up front.
 *
 * 2. **The money is the customer's until they no-show.** Whatever is collected
 *    credits toward the wash on arrival — it is a prepayment, not a fee. A
 *    customer who turns up pays exactly what they would have paid anyway.
 *
 * 3. **A free-cancel window, honoured to the second.** Cancel earlier than
 *    `freeCancelHours` before the slot and the whole prepayment comes back.
 *    Cancel later, or don't show, and it is forfeit — that is the entire point
 *    of collecting it.
 *
 * 4. **A refund returns a NUMBER, not money.** `cancellationOutcome` says how
 *    the prepayment splits; it deliberately does not say through which rail the
 *    customer gets their share, because no such rail exists yet — see the
 *    "NOT YET WIRED" section below, which is the reason this file is policy
 *    only. Kilat is the merchant of record and takes zero cut (ADR-0021), so
 *    whatever is built must return the customer's money without Kilat keeping
 *    any of it.
 *
 * Everything here is pure: no clock, no Firestore, no gateway. `nowMs` is always
 * a parameter, so every boundary is testable and the server and client cannot
 * disagree about what a customer was told they'd be charged.
 *
 * ## NOT YET WIRED, and the reason is not effort
 *
 * `bookTicket` does not call this yet, and enabling a non-`none` mode would
 * currently take money Kilat cannot give back. Establishing that took reading
 * the code rather than the plan:
 *
 *   - There is **no gateway refund rail.** Nothing in the toyyibPay integration
 *     reverses a charge.
 *   - The refund system that DOES exist explicitly does not move money.
 *     `apps/functions/src/payments/refunds.ts` says so in its own header: under
 *     the zero-cut model the WASH cash is already the operator's and Kilat never
 *     holds it, so a refund there is a RECORD — the operator hands cash back out
 *     of their till. A booking deposit is the opposite shape: the customer pays
 *     Kilat as merchant of record, so refunding one is a real transfer.
 *   - **There is no customer store-credit ledger** either, which is what the
 *     original proposal (SPEC D10) assumed the refund would land in. No balance,
 *     no entries, no redemption at POS.
 *
 * So the free-cancel window — the half of the policy that protects the CUSTOMER
 * — has nowhere to put the money. Shipping the charge without it would mean a
 * carwash could take a deposit and never return it, which is a worse product
 * than free bookings and a worse legal position than either.
 *
 * This module is the decision encoded and tested so it cannot drift while that
 * dependency is built. The wiring slice needs, in order: a customer credit
 * ledger (or a gateway refund rail), then `bookTicket` requiring the amount,
 * then the cancel/no-show transition splitting it. See
 * `docs/features/appointment-booking.feature.md`.
 */

export const AppointmentPaymentModeSchema = z.enum(['none', 'deposit', 'full']);
export type AppointmentPaymentMode = z.infer<typeof AppointmentPaymentModeSchema>;

export const AppointmentPaymentPolicySchema = z.object({
  /**
   * `none` — book for free, exactly as before (the default).
   * `deposit` — take `depositPercent` of the wash price up front.
   * `full` — take the whole price up front.
   */
  mode: AppointmentPaymentModeSchema.default('none'),
  /**
   * Percentage of the wash price taken as a deposit, 1-100.
   *
   * Only read when `mode` is `deposit`. Bounded at both ends on purpose: 0 would
   * be a deposit mode that collects nothing (say `none` instead, and mean it),
   * and over 100 would charge more than the wash costs.
   */
  depositPercent: z.number().int().min(1).max(100).default(20),
  /**
   * How far ahead of the slot a customer must cancel to be refunded in full.
   *
   * HIGHER IS STRICTER, which is the opposite of the first way most people read
   * it — and the first draft of this comment got it backwards until a test said
   * so. The value is a distance from the slot, not a length of leniency:
   *
   *   0    free cancellation right up to the slot time (most lenient)
   *   12   must cancel 12h ahead; inside that, the prepayment is forfeit
   *   168  a week ahead — and since bookings only open 7 days out, that is
   *        effectively "no refunds", expressed without a fourth mode
   *
   * Capped at 168 for exactly that reason: it is the whole booking window, so
   * nothing beyond it can mean anything different.
   */
  freeCancelHours: z.number().int().min(0).max(168).default(12),
});
export type AppointmentPaymentPolicy = z.infer<typeof AppointmentPaymentPolicySchema>;

/** The policy a tenant has until an operator changes it: charge nothing. */
export const DEFAULT_APPOINTMENT_PAYMENT_POLICY: AppointmentPaymentPolicy = {
  mode: 'none',
  depositPercent: 20,
  freeCancelHours: 12,
};

/**
 * What the customer must pay to hold this slot, in sen.
 *
 * Returns 0 when the feature is off — callers can branch on `> 0` rather than
 * re-reading the mode, so a new mode cannot be added without every caller
 * being forced to think about it here first.
 *
 * Rounding is DOWN, deliberately. A deposit is a fraction of a price the
 * customer has already been shown, and rounding up would charge them a sen more
 * than the arithmetic they can do themselves — a tiny discrepancy on a payment
 * screen costs more trust than it collects.
 */
export function requiredPrepaymentSen(
  policy: AppointmentPaymentPolicy,
  washTotalSen: number,
): number {
  if (!Number.isFinite(washTotalSen) || washTotalSen <= 0) return 0;
  const total = Math.floor(washTotalSen);

  switch (policy.mode) {
    case 'none':
      return 0;
    case 'full':
      return total;
    case 'deposit': {
      const sen = Math.floor((total * policy.depositPercent) / 100);
      // A percentage of a small total can floor to zero, and a payment intent
      // for RM 0.00 is a gateway error rather than a free booking. One sen is
      // the smallest thing that is still a payment.
      return Math.max(sen, 1);
    }
  }
}

export type CancellationReason = 'no_prepayment' | 'within_free_window' | 'late_cancel' | 'no_show';

export interface CancellationOutcome {
  /** Returned to the customer as store credit, in sen. */
  readonly refundSen: number;
  /** Kept by the operator, in sen. */
  readonly forfeitSen: number;
  /** Why it split this way — surfaced to both parties, never a silent deduction. */
  readonly reason: CancellationReason;
}

/**
 * What happens to the prepayment when an appointment ends without a wash.
 *
 * `kind` separates the two ways that happens, because they are not the same
 * promise: a CANCEL is the customer telling you in advance (and the free window
 * is the reward for doing so), while a NO_SHOW is the slot being wasted with no
 * warning and never earns a refund regardless of timing.
 *
 * Returning both halves and a reason — rather than just a number — is what lets
 * the UI tell a customer "RM 5.00 back as credit, RM 5.00 kept because you
 * cancelled inside the 12-hour window" instead of showing them a balance that
 * silently changed.
 */
export function cancellationOutcome(params: {
  readonly policy: AppointmentPaymentPolicy;
  readonly prepaidSen: number;
  readonly scheduledForMs: number;
  readonly nowMs: number;
  readonly kind: 'cancel' | 'no_show';
}): CancellationOutcome {
  const prepaid =
    Number.isFinite(params.prepaidSen) && params.prepaidSen > 0 ? Math.floor(params.prepaidSen) : 0;

  // Nothing was taken, so there is nothing to argue about. This is the path
  // every tenant is on until an operator turns the feature on.
  if (prepaid === 0) {
    return { refundSen: 0, forfeitSen: 0, reason: 'no_prepayment' };
  }

  if (params.kind === 'no_show') {
    return { refundSen: 0, forfeitSen: prepaid, reason: 'no_show' };
  }

  const windowMs = params.policy.freeCancelHours * 60 * 60 * 1000;
  const msUntilSlot = params.scheduledForMs - params.nowMs;

  // `>=` so cancelling EXACTLY on the boundary refunds. The boundary has to
  // fall on one side and it should be the customer's: a person cancelling at
  // what they believe is the last free moment must not be charged for being a
  // second slow.
  if (msUntilSlot >= windowMs) {
    return { refundSen: prepaid, forfeitSen: 0, reason: 'within_free_window' };
  }

  return { refundSen: 0, forfeitSen: prepaid, reason: 'late_cancel' };
}

/**
 * The moment after which cancelling stops being free, in epoch millis.
 *
 * The customer-facing counterpart of the rule above: a booking screen can say
 * "free cancellation until Tuesday 8pm" rather than "12 hours before", which is
 * arithmetic the customer has to do while deciding whether to trust you.
 */
export function freeCancelDeadlineMs(
  policy: AppointmentPaymentPolicy,
  scheduledForMs: number,
): number {
  return scheduledForMs - policy.freeCancelHours * 60 * 60 * 1000;
}
